
import { Context } from "svgcanvas";
import ChartDataLabels from 'chartjs-plugin-datalabels';
export default {
  data() {
    return {
      // Default color palettes (A-F shades) for matching
      defaultPalettes: {
        yellow: [
          'rgb(250 174 0)',    // A
          'rgb(251 189 44)',   // B
          'rgb(252 201 89)',   // C
          'rgb(253 215 133)',  // D
          'rgb(254 228 178)',  // E
          'rgb(255 242 222)',  // F
        ],
        green: [
          'rgb(152 203 46)',   // A
          'rgb(169 213 80)',   // B
          'rgb(185 224 110)',  // C
          'rgb(202 234 144)',  // D
          'rgb(218 245 176)',  // E
          'rgb(235 255 208)',  // F (note: original has trailing space, we handle both)
        ],
        blue: [
          'rgb(80 66 255)',    // A
          'rgb(104 113 255)',  // B
          'rgb(128 142 254)',  // C
          'rgb(159 173 255)',  // D
          'rgb(195 205 255)',  // E
          'rgb(232 236 255)',  // F
        ],
        purple: [
          'rgb(255 25 81)',    // A
          'rgb(255 66 112)',   // B
          'rgb(255 110 143)',  // C
          'rgb(255 152 175)',  // D
          'rgb(255 195 206)',  // E
          'rgb(255 237 237)',  // F
        ]
      },
      // Map color names to chart_colors keys
      colorKeyMap: {
        yellow: 'subscribers',
        green: 'revenue',
        blue: 'donors',
        purple: 'percentages'
      }
    }
  },
  methods: {
    getDate() {
      const today = new Date();

      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const day = String(today.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    /**
     * Get custom chart colors with priority:
     * 1. User's agency colors (if user is an agency user)
     * 2. Client colors
     * 3. Client's agency colors (fallback)
     * @returns {Object|null} Custom chart colors object or null
     */
    getCustomChartColors() {
      // Priority 1: User's agency colors (for agency users viewing client reports)
      const userAgencyColors = this.user?.agency?.chart_colors;
      if (userAgencyColors && Object.keys(userAgencyColors).length > 0) {
        return userAgencyColors;
      }

      // Priority 2: Client colors
      const clientColors = this.client?.chart_colors;

      // Priority 3: Client's agency colors (fallback)
      const clientAgencyColors = this.client?.agency?.chart_colors;

      if (clientColors && Object.keys(clientColors).length > 0) {
        // If client's agency colors exist, merge them as defaults (client overrides)
        if (clientAgencyColors && Object.keys(clientAgencyColors).length > 0) {
          return { ...clientAgencyColors, ...clientColors };
        }
        return clientColors;
      }

      // Fall back to client's agency colors
      return clientAgencyColors || null;
    },
    /**
     * Generate 6 shades from a base color (RGB string)
     * Mimics ChartColorService::generateShades
     * @param {string} baseColor - RGB string like "rgb(152 203 46)"
     * @returns {Array} Array of 6 RGB color strings from dark to light
     */
    generateShades(baseColor) {
      const rgb = this.parseRgbColor(baseColor);
      if (!rgb) return null;

      const shades = [];
      const hsl = this.rgbToHsl(rgb.r, rgb.g, rgb.b);

      // More dramatic lightness steps: A=0%, B=15%, C=30%, D=45%, E=60%, F=75%
      const lightnessSteps = [0, 0.15, 0.30, 0.45, 0.60, 0.75];

      for (let i = 0; i < 6; i++) {
        const lightnessIncrease = lightnessSteps[i];
        const newLightness = Math.min(0.95, hsl.l + (1 - hsl.l) * lightnessIncrease);
        const newRgb = this.hslToRgb(hsl.h, hsl.s, newLightness);
        shades.push(`rgb(${newRgb.r} ${newRgb.g} ${newRgb.b})`);
      }

      return shades;
    },
    /**
     * Parse color string to {r, g, b} object
     * Handles "rgb(152 203 46)", "rgb(152, 203, 46)", and "#98CB2E" formats
     */
    parseRgbColor(color) {
      if (!color) return null;

      // Try RGB format first
      const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*\)/i);
      if (rgbMatch) {
        return {
          r: parseInt(rgbMatch[1]),
          g: parseInt(rgbMatch[2]),
          b: parseInt(rgbMatch[3])
        };
      }

      // Try hex format (#RRGGBB or #RGB)
      const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
      if (hexMatch) {
        return {
          r: parseInt(hexMatch[1], 16),
          g: parseInt(hexMatch[2], 16),
          b: parseInt(hexMatch[3], 16)
        };
      }

      // Try short hex format (#RGB)
      const shortHexMatch = color.match(/^#?([a-f\d])([a-f\d])([a-f\d])$/i);
      if (shortHexMatch) {
        return {
          r: parseInt(shortHexMatch[1] + shortHexMatch[1], 16),
          g: parseInt(shortHexMatch[2] + shortHexMatch[2], 16),
          b: parseInt(shortHexMatch[3] + shortHexMatch[3], 16)
        };
      }

      return null;
    },
    /**
     * Convert RGB to HSL
     */
    rgbToHsl(r, g, b) {
      r /= 255;
      g /= 255;
      b /= 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
          case g:
            h = ((b - r) / d + 2) / 6;
            break;
          case b:
            h = ((r - g) / d + 4) / 6;
            break;
        }
      }

      return { h, s, l };
    },
    /**
     * Convert HSL to RGB
     */
    hslToRgb(h, s, l) {
      let r, g, b;

      if (s === 0) {
        r = g = b = l;
      } else {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1 / 6) return p + (q - p) * 6 * t;
          if (t < 1 / 2) return q;
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
          return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }

      return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
      };
    },
    /**
     * Detect color type and shade index from an RGB color value
     * @param {string} colorValue - RGB string
     * @returns {Object|null} { colorType: 'yellow'|'green'|'blue'|'purple', shadeIndex: 0-5 } or null
     */
    detectColorTypeAndShade(colorValue) {
      if (!colorValue) return null;

      const rgb = this.parseRgbColor(colorValue);
      if (!rgb) return null;

      // Check against each color palette
      for (const [colorType, palette] of Object.entries(this.defaultPalettes)) {
        for (let shadeIndex = 0; shadeIndex < palette.length; shadeIndex++) {
          const defaultRgb = this.parseRgbColor(palette[shadeIndex]);
          if (!defaultRgb) continue;

          // Check with tolerance (±5 for each component)
          const tolerance = 5;
          if (
            Math.abs(rgb.r - defaultRgb.r) <= tolerance &&
            Math.abs(rgb.g - defaultRgb.g) <= tolerance &&
            Math.abs(rgb.b - defaultRgb.b) <= tolerance
          ) {
            return { colorType, shadeIndex };
          }
        }
      }

      return null;
    },
    /**
     * Apply custom colors to chart datasets for export
     * Detects which default shade each color is and replaces with the corresponding custom shade
     * @param {Object} chartData - The chart data object with datasets
     * @returns {Object} Chart data with custom colors applied
     */
    applyCustomColorsForExport(chartData) {
      const customColors = this.getCustomChartColors();
      if (!customColors || Object.keys(customColors).length === 0) {
        return chartData; // No custom colors, return unchanged
      }

      const datasets = chartData.datasets || [];
      if (datasets.length === 0) {
        return chartData;
      }

      // Cache for generated custom shades by color type
      const customShadesCache = {};

      /**
       * Get custom shades for a color type, generating if needed
       */
      const getCustomShades = (colorType) => {
        if (customShadesCache[colorType]) {
          return customShadesCache[colorType];
        }

        const customColorKey = this.colorKeyMap[colorType];
        const customBaseColor = customColors[customColorKey];

        if (!customBaseColor) {
          return null;
        }

        const shades = this.generateShades(customBaseColor);
        customShadesCache[colorType] = shades;
        return shades;
      };

      /**
       * Replace a single color if it matches a default shade
       */
      const replaceColor = (originalColor) => {
        const detected = this.detectColorTypeAndShade(originalColor);
        if (!detected) {
          return originalColor; // Not a default color, leave unchanged
        }

        const { colorType, shadeIndex } = detected;
        const customShades = getCustomShades(colorType);

        if (!customShades) {
          return originalColor; // No custom color for this type
        }

        return customShades[shadeIndex] || customShades[0];
      };

      // Apply custom colors to all datasets
      datasets.forEach((dataset) => {
        // Update backgroundColor
        if (dataset.backgroundColor) {
          if (Array.isArray(dataset.backgroundColor)) {
            dataset.backgroundColor = dataset.backgroundColor.map(replaceColor);
          } else {
            dataset.backgroundColor = replaceColor(dataset.backgroundColor);
          }
        }

        // Update borderColor
        if (dataset.borderColor) {
          if (Array.isArray(dataset.borderColor)) {
            dataset.borderColor = dataset.borderColor.map(replaceColor);
          } else {
            dataset.borderColor = replaceColor(dataset.borderColor);
          }
        }

        // Update pointBackgroundColor (for line charts)
        if (dataset.pointBackgroundColor) {
          if (Array.isArray(dataset.pointBackgroundColor)) {
            dataset.pointBackgroundColor = dataset.pointBackgroundColor.map(replaceColor);
          } else {
            dataset.pointBackgroundColor = replaceColor(dataset.pointBackgroundColor);
          }
        }

        // Update pointBorderColor (for line charts)
        if (dataset.pointBorderColor) {
          if (Array.isArray(dataset.pointBorderColor)) {
            dataset.pointBorderColor = dataset.pointBorderColor.map(replaceColor);
          } else {
            dataset.pointBorderColor = replaceColor(dataset.pointBorderColor);
          }
        }
      });

      return chartData;
    },
    /**
     * Pie export legend sums/slices; values may be plain numbers or Chart-parsed objects.
     * Calling Number() on some objects throws (ToPrimitive failure).
     */
    pieExportSliceNumber(raw) {
      if (raw == null) {
        return 0;
      }
      if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : 0;
      }
      if (typeof raw === 'object') {
        if (typeof raw.y === 'number' && Number.isFinite(raw.y)) {
          return raw.y;
        }
        if (typeof raw.raw === 'number' && Number.isFinite(raw.raw)) {
          return raw.raw;
        }
        if (typeof raw.raw === 'string') {
          const p = parseFloat(raw.raw.replace(/,/g, ''));
          return Number.isFinite(p) ? p : 0;
        }
      }
      if (typeof raw === 'string') {
        const p = parseFloat(raw.replace(/,/g, ''));
        return Number.isFinite(p) ? p : 0;
      }
      return 0;
    },
    convertToSVG(c) {
      if (!c || typeof c.getContext !== 'function') {
        console.warn('convertToSVG: canvas element missing or not a canvas', c);
        return '';
      }
      var context = c.getContext("2d");
      let chart = Chart.getChart(c);

      // If the chart instance does not exist (e.g., the canvas was hidden and never initialised)
      // attempt to rebuild it quickly using a previously stored config reference.
      if (!chart && c.__chartConfigExport) {
        try {
          chart = new Chart(context, c.__chartConfigExport);
        } catch (e) {
          console.warn('Could not rebuild missing chart instance for export', e);
        }
      }

      if (!chart) {
        console.warn('Chart instance not found for canvas during SVG export', c);
        return '';
      }

      // Filter out hidden datasets before creating SVG
      let filteredData = this.getVisibleChartData(chart);

      // Apply custom colors for export (if client has custom chart_colors)
      filteredData = this.applyCustomColorsForExport(filteredData);

      // Create a config copy with filtered data
      let exportConfig = {
        type: chart.config.type,
        data: filteredData,
        options: { ...chart.config.options }
      };

      // Export-only: force line datasets to draw using direct path logic.
      // canvas2svg can miss lines when Chart.js uses cached Path2D stroke calls.
      if (exportConfig.type === 'line') {
        // Export-only: force point markers visible even when interactive chart hides them.
        // The logs showed pointRadius=0, which means no circles are emitted into the SVG.
        exportConfig.data.datasets = (exportConfig.data.datasets || []).map((ds) => {
          if ((ds.type || exportConfig.type) !== 'line') {
            return ds;
          }

          const pointRadius = Number(ds.pointRadius ?? 0);
          const pointHoverRadius = Number(ds.pointHoverRadius ?? 0);

          return {
            ...ds,
            pointRadius: pointRadius > 0 ? pointRadius : 4,
            pointHoverRadius: pointHoverRadius > 0 ? pointHoverRadius : 4,
            pointHitRadius: Number(ds.pointHitRadius ?? 0) > 0 ? ds.pointHitRadius : 6,
            pointBorderWidth: Number(ds.pointBorderWidth ?? 0) > 0 ? ds.pointBorderWidth : 1,
            pointBackgroundColor: ds.pointBackgroundColor || ds.borderColor || ds.backgroundColor,
            pointBorderColor: ds.pointBorderColor || ds.borderColor || ds.backgroundColor,
          };
        });

        exportConfig.options.datasets = exportConfig.options.datasets || {};
        exportConfig.options.datasets.line = {
          ...(exportConfig.options.datasets.line || {}),
          segment: {
            ...(exportConfig.options.datasets?.line?.segment || {}),
          },
        };
      }

      // Ensure datalabels still render correctly after filtering datasets
      this.wrapDatalabelFormatter(exportConfig, chart, filteredData);

      // deactivate responsiveness and animation
      exportConfig.options.responsive = false;
      exportConfig.options.animation = false;
      exportConfig.options.devicePixelRatio = 1;

      const pieTypes = ['pie', 'doughnut'];
      if (pieTypes.includes(exportConfig.type)) {
        const pieLegendFormatFn =
          (typeof c.__chartConfigExport?.options?.plugins?.avidExportLegend?.formatValue === 'function'
            ? c.__chartConfigExport.options.plugins.avidExportLegend.formatValue
            : null) ||
          (typeof chart.config?.options?.plugins?.avidExportLegend?.formatValue === 'function'
            ? chart.config.options.plugins.avidExportLegend.formatValue
            : null);
        const prevPlugins = exportConfig.options.plugins || {};
        const prevLegend = prevPlugins.legend;
        if (!prevLegend || prevLegend.display === false) {
          exportConfig.options.plugins = {
            ...prevPlugins,
            legend: {
              ...(prevLegend || {}),
              display: true,
              position: prevLegend?.position || 'right',
              labels: {
                boxWidth: 12,
                boxHeight: 12,
                padding: 10,
                font: { size: 11, family: 'DM Sans, Arial, sans-serif' },
                generateLabels: (exportChart) => {
                  const cd = exportChart.data;
                  const ds = cd.datasets[0];
                  if (!ds) {
                    return [];
                  }
                  const meta = exportChart.getDatasetMeta(0);
                  const vals = ds.data || [];
                  const labs = cd.labels || [];
                  const total = vals.reduce((s, x) => s + this.pieExportSliceNumber(x), 0);
                  return labs.map((lab, i) => {
                    const v = this.pieExportSliceNumber(vals[i]);
                    const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
                    const bg = Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor;
                    const fmt =
                      pieLegendFormatFn ||
                      exportChart.options?.plugins?.avidExportLegend?.formatValue;
                    const valueStr = typeof fmt === 'function' ? fmt(v) : v.toLocaleString();
                    return {
                      text: `${lab}: ${valueStr} (${pct}%)`,
                      fillStyle: bg,
                      strokeStyle: bg,
                      lineWidth: 0,
                      hidden: !!(meta.data[i]?.hidden),
                      index: i,
                      datasetIndex: 0,
                    };
                  });
                },
              },
            },
          };
        }
      }

      // canvas2svg 'mock' context
      const width = Number(c.dataset.width || c.offsetWidth || c.width) || 0;
      const height = Number(c.dataset.height || c.offsetHeight || c.height) || 0;
      var svgContext = Context({ width: width, height: height });

      // Create a temporary Chart instance on the mock context with filtered data

      // Wrap context methods to prevent NaN/Infinity values from reaching the SVG attributes
      // which can cause browser-level errors during SVG string generation.
      const wrapMethod = (obj, name) => {
        const original = obj[name];
        if (typeof original !== 'function') return;
        obj[name] = function (...args) {
          // Check if any numerical argument is NaN or Infinity
          if (args.some(arg => typeof arg === 'number' && !isFinite(arg))) {
            return;
          }
          return original.apply(this, args);
        };
      };

      // Numerical methods sensitive to non-finite values
      [
        'arc', 'arcTo', 'bezierCurveTo', 'lineTo', 'moveTo', 'quadraticCurveTo', 'rect',
        'fillText', 'strokeText', 'setTransform', 'transform', 'translate', 'scale', 'rotate',
        'fillRect', 'strokeRect', 'clearRect'
      ].forEach(m => wrapMethod(svgContext, m));

      // canvas2svg doesn't support stroke(Path2D) / fill(Path2D).
      // Replace Path2D with a recording proxy that replays commands onto the
      // SVG context so both lines AND points render correctly.
      class RecordingPath2D {
        constructor(source) {
          this._cmds = [];
          if (source != null) {
            if (source instanceof RecordingPath2D && source._cmds) {
              this._cmds = source._cmds.map(c => [...c]);
            }
          }
        }
        moveTo(x, y) { this._cmds.push(['moveTo', x, y]); }
        lineTo(x, y) { this._cmds.push(['lineTo', x, y]); }
        arc(x, y, r, s, e, ccw) { this._cmds.push(['arc', x, y, r, s, e, ccw]); }
        arcTo(x1, y1, x2, y2, r) { this._cmds.push(['arcTo', x1, y1, x2, y2, r]); }
        bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y) { this._cmds.push(['bezierCurveTo', cp1x, cp1y, cp2x, cp2y, x, y]); }
        quadraticCurveTo(cpx, cpy, x, y) { this._cmds.push(['quadraticCurveTo', cpx, cpy, x, y]); }
        closePath() { this._cmds.push(['closePath']); }
        rect(x, y, w, h) { this._cmds.push(['rect', x, y, w, h]); }
        ellipse(x, y, rx, ry, rot, s, e, ccw) { this._cmds.push(['ellipse', x, y, rx, ry, rot, s, e, ccw]); }
        roundRect(x, y, w, h, radii) {
          // roundRect optional; fallback: rect then closePath for simple shape
          this._cmds.push(['rect', x, y, w, h]);
          this._cmds.push(['closePath']);
        }
        addPath(path, transform) {
          if (path instanceof RecordingPath2D && path._cmds) {
            for (const cmd of path._cmds) {
              this._cmds.push([...cmd]);
            }
          }
        }
        replay(ctx) {
          ctx.beginPath();
          for (const [method, ...args] of this._cmds) {
            if (typeof ctx[method] === 'function') {
              ctx[method](...args);
            }
          }
        }
      }

      const origStroke = svgContext.stroke.bind(svgContext);
      const origFill = svgContext.fill.bind(svgContext);

      svgContext.stroke = function (pathOrRule) {
        if (pathOrRule instanceof RecordingPath2D) {
          pathOrRule.replay(svgContext);
          origStroke();
        } else {
          origStroke();
        }
      };

      svgContext.fill = function (pathOrRule, fillRule) {
        if (pathOrRule instanceof RecordingPath2D) {
          pathOrRule.replay(svgContext);
          if (fillRule !== undefined && typeof svgContext.fillRule !== 'undefined') {
            svgContext.fillRule = fillRule;
          }
          if (fillRule !== undefined) {
            origFill(fillRule);
          } else {
            origFill();
          }
        } else {
          if (pathOrRule !== undefined) {
            origFill(pathOrRule);
          } else {
            origFill();
          }
        }
      };

      const originalPath2D = window.Path2D;
      window.Path2D = RecordingPath2D;

      var mySvg;
      try {
        mySvg = new Chart(svgContext, {
          ...exportConfig,
          plugins: [ChartDataLabels]
        });
      } finally {
        window.Path2D = originalPath2D;
      }

      // Convert to SVG string
      let svgString = svgContext.getSerializedSvg(false);

      // Clean up the temporary Chart instance to prevent it from being
      // tracked by Chart.js' global animator. Otherwise, Chart.js may try
      // to update the instance later and throw errors because the mock
      // context does not contain a real canvas element (which leads to
      // "Cannot read properties of null (reading 'getContext')" in
      // clearCanvas). Destroying the instance removes it from the animator
      // queue and frees memory.
      try {
        mySvg.destroy();
      } catch (e) {
        // Silently ignore any errors during cleanup – the important part
        // is that we attempted to destroy the chart to detach it from the
        // animation loop.
      }

      // ✅ Normalize for PowerPoint compatibility
      svgString = this.normalizeSvgForOffice(svgString);

      // (Optional) your font patches
      svgString = this.updateTextElements(svgString);

      svgString = this.alignLegendMarkersInSvgString(svgString);

      if (exportConfig.type === 'bar' && exportConfig.options?.indexAxis === 'y') {
        svgString = this.nudgeHorizontalBarValueLabelsForOffice(svgString);
      }

      return svgString;

    },
    /**
     * Get visible chart data by filtering out hidden datasets
     * @param {Chart} chart - The Chart.js instance
     * @returns {Object} Filtered data object with only visible datasets
     */
    getVisibleChartData(chart) {
      const currentDatasets = chart.data.datasets || [];

      // Filter to only include visible datasets
      const visibleDatasets = currentDatasets.filter((ds, index) => {
        // Check dataset meta for visibility state (most reliable method)
        try {
          const meta = chart.getDatasetMeta ? chart.getDatasetMeta(index) : null;
          if (meta && (meta.hidden === true || meta.hidden === 'true')) {
            return false;
          }
        } catch (e) {
          // If getDatasetMeta fails, continue with other checks
        }

        // Use Chart.js API to check if dataset is actually visible
        try {
          if (chart.isDatasetVisible && typeof chart.isDatasetVisible === 'function') {
            if (!chart.isDatasetVisible(index)) {
              return false;
            }
          }
        } catch (e) {
          // If isDatasetVisible fails, continue with other checks
        }

        // Also check the hidden property on the dataset itself
        if (ds.hidden === true || ds.hidden === 'true') {
          return false;
        }

        return true;
      });

      // Create filtered data object and keep track of original indices
      return {
        labels: chart.data.labels || [],
        datasets: visibleDatasets.map((ds, index) => {
          const cloned = JSON.parse(JSON.stringify(ds));
          // Remove hidden property to ensure clean state
          delete cloned.hidden;
          // Track original dataset index for formatter logic
          cloned.__originalIndex = chart.data.datasets.indexOf(ds);
          return cloned;
        })
      };
    },
    /**
     * Wrap datalabel formatter so "last dataset" logic works with filtered datasets
     */
    wrapDatalabelFormatter(exportConfig, chart, filteredData) {
      const originalOptions = chart.config.options || {};
      const originalDatalabels = originalOptions.plugins?.datalabels;
      if (!originalDatalabels || !originalDatalabels.formatter) {
        return;
      }

      const originalFormatter = originalDatalabels.formatter;

      const safeFormatter = function (value, context) {
        const dataset = filteredData.datasets[context.datasetIndex] || {};
        const originalIndex = dataset.__originalIndex ?? context.datasetIndex;

        // Use original meta visibility
        try {
          const meta = chart.getDatasetMeta ? chart.getDatasetMeta(originalIndex) : null;
          if (meta && (meta.hidden === true || meta.hidden === 'true')) {
            return null;
          }
          if (chart.isDatasetVisible && typeof chart.isDatasetVisible === 'function') {
            if (!chart.isDatasetVisible(originalIndex)) {
              return null;
            }
          }
        } catch (e) {
          // continue
        }

        try {
          // Build fake context using original dataset index and original chart data
          const fakeContext = {
            ...context,
            datasetIndex: originalIndex,
            chart: {
              ...context.chart,
              data: chart.data
            }
          };

          return originalFormatter.call(this, value, fakeContext);
        } catch (e) {
          return null;
        }
      };

      exportConfig.options.plugins = exportConfig.options.plugins || {};
      exportConfig.options.plugins.datalabels = {
        ...originalDatalabels,
        formatter: safeFormatter
      };
    },
    sanitizeNaNPaths(svg) {
      const needsClose = d => !/[zZ]\s*$/.test(d);

      svg.querySelectorAll('path[d]').forEach(p => {
        const d = p.getAttribute('d');
        if (!d) return;

        // If any NaN/Infinity inside the 'd', try to salvage it
        const badIdx = d.search(/\b(?:NaN|Infinity)\b/i);
        if (badIdx !== -1) {
          // 1) Trim off everything from the first NaN/Infinity onward
          let d2 = d.slice(0, badIdx).trim();

          // 2) If we trimmed away the end, close the path so PPT gets a valid shape
          if (d2) {
            if (needsClose(d2)) d2 = `${d2} Z`;
            p.setAttribute('d', d2);
          } else {
            // If nothing salvageable, remove the path (last resort)
            p.parentNode && p.parentNode.removeChild(p);
          }
        }
      });
    },
    updateTextElements(svgString) {
      // Create a new DOMParser
      const parser = new DOMParser();
      // Parse the SVG string into an XML document
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

      // Get the root SVG element
      const svg = svgDoc.documentElement;
      // Find all <text> elements
      const textElements = svg.querySelectorAll('text');

      textElements.forEach(text => {
        // Check if font-size is not set or empty
        if (!text.getAttribute('font-size') || text.getAttribute('font-size') === '') {
          text.setAttribute('font-size', '15px');
        }

        // Check if font-family is set to "DM Sans"
        if (text.getAttribute('font-family') === 'DM Sans') {
          text.setAttribute('font-family', 'DM Sans, "Franklin Gothic Medium", Arial');
        }
      });

      svg.querySelectorAll('*').forEach(el => {
        ['fill', 'stroke', 'stop-color'].forEach(attr => this.fixPaintAttr(el, attr));
        this.scrubNumericAttrs(el);
      });

      // Serialize the SVG back to a string
      const serializer = new XMLSerializer();
      return serializer.serializeToString(svg);
    },
    scrubNumericAttrs(el) {
      // attributes where NaN/Infinity are most harmful
      const suspect = ['x', 'y', 'cx', 'cy', 'r', 'rx', 'ry', 'x1', 'y1', 'x2', 'y2', 'width', 'height', 'stroke-width', 'font-size', 'opacity', 'fill-opacity', 'stroke-opacity', 'transform', 'd'];
      for (const name of suspect) {
        const v = el.getAttribute(name);
        if (!v) continue;
        // If it's a path 'd' and contains NaN, let the path-removal logic handle it.
        if (name === 'd') continue;
        if (/NaN|Infinity/i.test(v)) {
          // safest: drop the attribute; if width/height becomes invalid, the element won't render
          el.removeAttribute(name);
        }
      }

      // Also clean inline style values with NaN/Infinity
      const style = el.getAttribute('style');
      if (style && /NaN|Infinity/i.test(style)) {
        el.setAttribute('style', style.replace(/[-+]?(?:Infinity|NaN)\b/g, ''));
      }
    },
    addLinesToChart(svgString) {
      // Create a new DOMParser
      const parser = new DOMParser();

      // Parse the SVG string into an XML document
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

      // Get the root SVG element
      const svg = svgDoc.documentElement;

      // Find all <g> elements that contain the points
      const pointGroups = svg.querySelectorAll('g > g');

      // Loop through each group and extract points
      pointGroups.forEach(group => {
        const points = Array.from(group.querySelectorAll('path[fill]'));
        if (points.length < 2) return;

        // Build the path data
        let pathData = '';
        points.forEach((point, index) => {
          // Extract the coordinates and radius from the d attribute
          const coords = point.getAttribute('d').match(/M (\d+(\.\d+)?) (\d+(\.\d+)?)/);
          const radiusMatch = point.getAttribute('d').match(/A (\d+(\.\d+)?)/);

          if (coords && radiusMatch) {
            const x = parseFloat(coords[1]);
            const y = parseFloat(coords[3]);
            const radius = parseFloat(radiusMatch[1]);

            const centerX = x - radius;
            const centerY = y;

            if (isNaN(centerX) || isNaN(centerY)) return;

            if (index === 0) {
              pathData += `M ${centerX} ${centerY}`;
            } else {
              pathData += ` L ${centerX} ${centerY}`;
            }
          }
        });

        // Create a new path element
        const linePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        linePath.setAttribute('d', pathData);
        linePath.setAttribute('fill', 'none');
        linePath.setAttribute('stroke', group.querySelector('path[stroke]').getAttribute('stroke'));
        linePath.setAttribute('stroke-width', '1');

        // Append the new path to the same group as the points
        group.appendChild(linePath);
      });

      // Serialize the SVG back to a string
      const serializer = new XMLSerializer();
      return serializer.serializeToString(svg);
    },
    rgbPartsToHex(r, g, b) {
      const toHex = n => {
        const x = Math.max(0, Math.min(255, parseInt(n, 10)));
        return x.toString(16).padStart(2, '0');
      };
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    },
    // Helper to fix a paint attribute (fill/stroke/stop-color) value and split opacity if rgba()
    fixPaintAttr(el, attrName) {
      const val = el.getAttribute(attrName);
      if (!val) return;

      const trimmedVal = val.trim();
      if (!trimmedVal) {
        return;
      }

      // a) space-separated rgb() -> comma-separated
      //    rgb(152 203 46) -> rgb(152,203,46)
      let newVal = trimmedVal.replace(
        /rgb\(\s*(\d+)\s+(\d+)\s+(\d+)\s*\)/gi,
        (_m, r, g, b) => `rgb(${r},${g},${b})`
      );

      // b) rgba(r,g,b,a) or rgba(r g b / a) -> hex + *-opacity
      //    we handle both comma and space (CSS4) syntaxes
      const rgbaMatch = newVal.match(
        /rgba\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s\/]+([0-9.]+))\s*\)/i
      );
      if (rgbaMatch) {
        const [, r, g, b, aRaw] = rgbaMatch;
        const hex = rgbPartsToHex(r, g, b);
        const a = Math.max(0, Math.min(1, parseFloat(aRaw ?? '1')));
        newVal = hex;

        // map opacity to the right attribute
        const opacityAttr =
          attrName === 'fill' ? 'fill-opacity'
            : attrName === 'stroke' ? 'stroke-opacity'
              : attrName === 'stop-color' ? 'stop-opacity'
                : null;

        if (opacityAttr) el.setAttribute(opacityAttr, String(a));
      }

      // c) If we still have rgb(), convert it to hex for maximum PPT compatibility
      newVal = newVal.replace(
        /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/gi,
        (_m, r, g, b) => this.rgbPartsToHex(r, g, b)
      );

      el.setAttribute(attrName, typeof newVal === 'string' ? newVal.trim() : newVal);
    },
    /**
     * PowerPoint often skips pie "disk" paths that use a single elliptical arc
     * whose endpoint equals the start (360°). Browsers fill that; Office does not.
     * Chart.js/canvas2svg emits that for a 100% single-slice pie. Replace with <circle>.
     */
    replaceDegenerateFullCirclePiePaths(svg) {
      const ns = 'http://www.w3.org/2000/svg';
      svg.querySelectorAll('path[d]').forEach(p => {
        if (p.closest('defs')) {
          return;
        }
        const d = (p.getAttribute('d') || '').replace(/\s+/g, ' ').trim();
        const m = d.match(
          /^M\s*([\d.-]+)\s+([\d.-]+)\s+A\s*([\d.-]+)\s+([\d.-]+)\s*([\d.-]+)\s*([01])\s*([01])\s*([\d.-]+)\s+([\d.-]+)/i
        );
        if (!m) {
          return;
        }
        const afterFirstArc = d.slice(m[0].length).trim();
        if (afterFirstArc.replace(/^Z\s*$/i, '').length > 0) {
          return;
        }
        const mx = parseFloat(m[1], 10);
        const my = parseFloat(m[2], 10);
        const rx = parseFloat(m[3], 10);
        const ry = parseFloat(m[4], 10);
        const ex = parseFloat(m[8], 10);
        const ey = parseFloat(m[9], 10);
        if (Math.abs(rx - ry) > 1e-2 || rx <= 0) {
          return;
        }
        if (Math.hypot(ex - mx, ey - my) > 1) {
          return;
        }
        let fill = p.getAttribute('fill');
        if (!fill || fill === 'none') {
          const style = p.getAttribute('style') || '';
          const fm = style.match(/fill:\s*([^;]+)/i);
          if (fm) {
            fill = fm[1].trim();
          }
        }
        if (!fill || fill === 'none') {
          return;
        }
        const cx = mx;
        const cy = my + ry;
        const circle = svg.ownerDocument.createElementNS(ns, 'circle');
        circle.setAttribute('cx', String(cx));
        circle.setAttribute('cy', String(cy));
        circle.setAttribute('r', String(rx));
        circle.setAttribute('fill', fill);
        const stroke = p.getAttribute('stroke');
        if (stroke && stroke !== 'none') {
          circle.setAttribute('stroke', stroke);
          const sw = p.getAttribute('stroke-width');
          if (sw) {
            circle.setAttribute('stroke-width', sw);
          }
        }
        p.parentNode.replaceChild(circle, p);
      });
    },
    /**
     * Legend marker may be <g><circle|rect|path></g><g><text> or <g><rect/><g><text/> as siblings.
     */
    findLegendLabelTextAfterMarker(markerEl) {
      const isTextEl = (el) => el && el.localName === 'text' && (el.textContent || '').trim();
      const p1 = markerEl.parentElement;
      if (p1 && p1.nextElementSibling) {
        const s = p1.nextElementSibling;
        if (isTextEl(s)) {
          return s;
        }
        const t = s.querySelector && s.querySelector('text');
        if (t && (t.textContent || '').trim()) {
          return t;
        }
      }
      if (markerEl.nextElementSibling) {
        const s = markerEl.nextElementSibling;
        if (isTextEl(s)) {
          return s;
        }
        const t = s.querySelector && s.querySelector('text');
        if (t && (t.textContent || '').trim()) {
          return t;
        }
      }
      return null;
    },
    /**
     * Legend clip bottom is often exactly cy+r (e.g. 470) while circles extend to ~471 → flat bottoms in Office.
     * Only bumps clips whose max Y is in the bottom band so plot-area clips are untouched.
     */
    expandLegendMarkerClipPaths(svg) {
      const pathDMaxY = (d) => {
        const nums = d.match(/-?\d+\.?\d*/g) || [];
        let maxY = -Infinity;
        for (let i = 1; i < nums.length; i += 2) {
          const y = parseFloat(nums[i]);
          if (!Number.isNaN(y)) {
            maxY = Math.max(maxY, y);
          }
        }
        return maxY;
      };
      const bumpPathBottomY = (d, oldMaxY, newBottom) => {
        const esc = String(oldMaxY).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return d.replace(
          new RegExp(`([ML])\\s+(-?\\d+\\.?\\d*)\\s+(${esc})(?=\\s|Z|$)`, 'gi'),
          (_, cmd, x) => `${cmd} ${x} ${newBottom}`
        );
      };

      const legendCircles = [...svg.querySelectorAll('circle')].filter((c) => {
        const r = parseFloat(c.getAttribute('r') || '0');
        if (r < 5 || r > 14) {
          return false;
        }
        const p1 = c.parentElement;
        return !!(p1 && p1.nextElementSibling && p1.nextElementSibling.querySelector && p1.nextElementSibling.querySelector('text'));
      });
      const legendLinePaths = [...svg.querySelectorAll('path[d]')].filter((p) => {
        const seg = this.parseHorizontalLegendLineSegment(p.getAttribute('d') || '');
        if (!seg) {
          return false;
        }
        const p1 = p.parentElement;
        return !!(p1 && p1.nextElementSibling && p1.nextElementSibling.querySelector && p1.nextElementSibling.querySelector('text'));
      });
      const legendRects = [...svg.querySelectorAll('rect')].filter((r) => {
        const w = parseFloat(r.getAttribute('width') || '0');
        const h = parseFloat(r.getAttribute('height') || '0');
        if (w < 6 || w > 28 || h < 6 || h > 28) {
          return false;
        }
        return !!this.findLegendLabelTextAfterMarker(r);
      });
      const legendPathSwatches = [...svg.querySelectorAll('path[d]')].filter((p) => {
        if (p.closest('defs')) {
          return false;
        }
        const d = p.getAttribute('d') || '';
        if (this.parseHorizontalLegendLineSegment(d)) {
          return false;
        }
        const fill = p.getAttribute('fill');
        if (!fill || fill === 'none') {
          return false;
        }
        if (!this.legendSwatchPathFromMoveLineCommands(d)) {
          return false;
        }
        return !!this.findLegendLabelTextAfterMarker(p);
      });

      if (!legendCircles.length && !legendLinePaths.length && !legendRects.length && !legendPathSwatches.length) {
        return { expanded: false, clipBottom: null };
      }

      const fromCircles = legendCircles.length
        ? Math.max(...legendCircles.map((c) => parseFloat(c.getAttribute('cy')) + parseFloat(c.getAttribute('r')))) + 6
        : -Infinity;
      const fromLines = legendLinePaths.length
        ? Math.max(...legendLinePaths.map((p) => {
          const seg = this.parseHorizontalLegendLineSegment(p.getAttribute('d') || '');
          const sw = parseFloat(p.getAttribute('stroke-width') || '1') || 1;
          return (seg ? seg.y : 0) + sw / 2 + 14;
        }))
        : -Infinity;
      const fromRects = legendRects.length
        ? Math.max(...legendRects.map((r) => parseFloat(r.getAttribute('y') || '0') + parseFloat(r.getAttribute('height') || '0'))) + 10
        : -Infinity;
      const fromPathSw = legendPathSwatches.length
        ? Math.max(...legendPathSwatches.map((p) => {
          const bb = this.legendSwatchPathFromMoveLineCommands(p.getAttribute('d') || '');
          return bb ? bb.maxY + 12 : 0;
        }))
        : -Infinity;
      const needBottom = Math.max(fromCircles, fromLines, fromRects, fromPathSw, 0);
      const seen = new Set();
      let expanded = false;
      let clipBottom = null;
      const bumpClipsFrom = (startEl) => {
        let el = startEl;
        while (el && el !== svg) {
          const url = el.getAttribute && el.getAttribute('clip-path');
          if (url) {
            const m = url.match(/#([\w-]+)/);
            if (m && !seen.has(m[1])) {
              seen.add(m[1]);
              const clipEl = svg.querySelector(`#${m[1]}`);
              if (clipEl && clipEl.localName === 'clipPath') {
                clipEl.querySelectorAll('path[d]').forEach((path) => {
                  const d = path.getAttribute('d');
                  if (!d) {
                    return;
                  }
                  const maxY = pathDMaxY(d);
                  if (maxY >= 400 && needBottom > maxY + 0.25) {
                    const nb = Math.round(needBottom * 1000) / 1000;
                    path.setAttribute('d', bumpPathBottomY(d, maxY, nb));
                    expanded = true;
                    clipBottom = nb;
                  }
                });
              }
            }
          }
          el = el.parentElement;
        }
      };
      legendCircles.forEach((c) => bumpClipsFrom(c.parentElement));
      legendLinePaths.forEach((p) => bumpClipsFrom(p.parentElement));
      legendRects.forEach((r) => bumpClipsFrom(r.parentElement));
      legendPathSwatches.forEach((p) => bumpClipsFrom(p.parentElement));
      return { expanded, clipBottom };
    },
    /**
     * Line-chart legend: short horizontal stroke (M x1 y L x2 y), not circles.
     */
    /**
     * Pie/doughnut legend swatches use rounded-rect paths (Chart.js addRoundedRectPath), not <rect>.
     */
    legendSwatchPathFromMoveLineCommands(d) {
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      const bump = (x, y) => {
        if (Number.isNaN(x) || Number.isNaN(y)) {
          return;
        }
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      };
      let m;
      const reMl = /[MmLl]\s*(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;
      while ((m = reMl.exec(d)) !== null) {
        bump(parseFloat(m[1]), parseFloat(m[2]));
      }
      const reA = /A\s+(-?\d+\.?\d*)\s+(-?\d+\.?\d*)\s+(-?\d+\.?\d*)\s+([01])\s+([01])\s+(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/gi;
      while ((m = reA.exec(d)) !== null) {
        bump(parseFloat(m[6]), parseFloat(m[7]));
      }
      if (!Number.isFinite(minX)) {
        return null;
      }
      const w = maxX - minX;
      const h = maxY - minY;
      if (w < 4 || w > 40 || h < 4 || h > 40) {
        return null;
      }
      return { minX, maxX, minY, maxY, midY: (minY + maxY) / 2 };
    },
    parseHorizontalLegendLineSegment(d) {
      const t = (d || '').replace(/\s+/g, ' ').trim();
      const m = t.match(/^M\s*(-?\d+\.?\d*)\s+(-?\d+\.?\d*)\s+L\s*(-?\d+\.?\d*)\s+(-?\d+\.?\d*)\s*$/i);
      if (!m) {
        return null;
      }
      const x1 = parseFloat(m[1]);
      const y1 = parseFloat(m[2]);
      const x2 = parseFloat(m[3]);
      const y2 = parseFloat(m[4]);
      if (Math.abs(y1 - y2) > 1 || Number.isNaN(x1) || Number.isNaN(y1)) {
        return null;
      }
      const len = Math.abs(x2 - x1);
      if (len < 6 || len > 100) {
        return null;
      }
      const y = (y1 + y2) / 2;
      if (y < 380) {
        return null;
      }
      return { x1: Math.min(x1, x2), x2: Math.max(x1, x2), y };
    },
    /**
     * Horizontal bar value labels use dominant-baseline middle + matrix translate; PowerPoint
     * aligns like alphabetic baseline, so text sits high on the bar. Nudge ty like pie legend.
     */
    nudgeHorizontalBarValueLabelsForOffice(svgString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement;
      const isDatalabelFill = (f) => {
        const s = (f || '').toLowerCase().replace(/\s/g, '');
        return s === '#5f687c' || s === 'rgb(95,104,124)';
      };
      svg.querySelectorAll('text').forEach((textEl) => {
        if (!isDatalabelFill(textEl.getAttribute('fill'))) {
          return;
        }
        if (textEl.getAttribute('text-anchor') !== 'start') {
          return;
        }
        const tr = textEl.getAttribute('transform') || '';
        const m = tr.match(/matrix\s*\(\s*1\s+0\s+0\s+1\s+(-?[\d.e+-]+)\s+(-?[\d.e+-]+)\s*\)/i);
        if (!m) {
          return;
        }
        const fs = parseFloat(String(textEl.getAttribute('font-size') || '15').replace(/px/gi, '')) || 15;
        const nudge = fs * 0.42;
        const tx = parseFloat(m[1]);
        const ty = parseFloat(m[2]) + nudge;
        textEl.setAttribute('transform', `matrix(1 0 0 1 ${Math.round(tx * 1000) / 1000} ${Math.round(ty * 1000) / 1000})`);
        textEl.removeAttribute('dominant-baseline');
      });
      return new XMLSerializer().serializeToString(svg);
    },
    /**
     * svgcanvas legend: wrong text y/x vs circles; Office mishandles dominant-baseline.
     * Expand bottom clip so circles aren’t flat; nudge y for alphabetic-baseline-style centering.
     */
    alignLegendMarkersInSvgString(svgString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement;
      this.expandLegendMarkerClipPaths(svg);
      const pad = 10;
      svg.querySelectorAll('circle').forEach((c) => {
        const r = parseFloat(c.getAttribute('r') || '0');
        if (r < 5 || r > 14) {
          return;
        }
        const p1 = c.parentElement;
        if (!p1 || !p1.nextElementSibling) {
          return;
        }
        const textEl = p1.nextElementSibling.querySelector && p1.nextElementSibling.querySelector('text');
        if (!textEl || !(textEl.textContent || '').trim()) {
          return;
        }
        const cy = parseFloat(c.getAttribute('cy') || '');
        const cx = parseFloat(c.getAttribute('cx') || '');
        if (Number.isNaN(cy) || Number.isNaN(cx)) {
          return;
        }
        const fs = parseFloat(String(textEl.getAttribute('font-size') || '12').replace(/px/gi, '')) || 12;
        const officeBaselineNudge = fs * 0.4;
        textEl.setAttribute('y', String(Math.round((cy + officeBaselineNudge) * 1000) / 1000));
        textEl.setAttribute('x', String(Math.round((cx + r + pad) * 1000) / 1000));
        textEl.removeAttribute('dominant-baseline');
      });
      svg.querySelectorAll('rect').forEach((rectEl) => {
        const rw = parseFloat(rectEl.getAttribute('width') || '0');
        const rh = parseFloat(rectEl.getAttribute('height') || '0');
        if (rw < 6 || rw > 28 || rh < 6 || rh > 28) {
          return;
        }
        const textEl = this.findLegendLabelTextAfterMarker(rectEl);
        if (!textEl) {
          return;
        }
        const rx = parseFloat(rectEl.getAttribute('x') || '');
        const ry = parseFloat(rectEl.getAttribute('y') || '');
        if (Number.isNaN(rx) || Number.isNaN(ry)) {
          return;
        }
        const midY = ry + rh / 2;
        const fs = parseFloat(String(textEl.getAttribute('font-size') || '12').replace(/px/gi, '')) || 12;
        const officeBaselineNudge = fs * 0.4;
        textEl.setAttribute('y', String(Math.round((midY + officeBaselineNudge) * 1000) / 1000));
        textEl.setAttribute('x', String(Math.round((rx + rw + pad) * 1000) / 1000));
        textEl.removeAttribute('dominant-baseline');
      });
      svg.querySelectorAll('path[d]').forEach((pathEl) => {
        if (pathEl.closest('defs')) {
          return;
        }
        const d = pathEl.getAttribute('d') || '';
        if (this.parseHorizontalLegendLineSegment(d)) {
          return;
        }
        const fill = pathEl.getAttribute('fill');
        if (!fill || fill === 'none') {
          return;
        }
        const bb = this.legendSwatchPathFromMoveLineCommands(d);
        if (!bb) {
          return;
        }
        const textEl = this.findLegendLabelTextAfterMarker(pathEl);
        if (!textEl) {
          return;
        }
        const fs = parseFloat(String(textEl.getAttribute('font-size') || '12').replace(/px/gi, '')) || 12;
        const officeBaselineNudge = fs * 0.4;
        textEl.setAttribute('y', String(Math.round((bb.midY + officeBaselineNudge) * 1000) / 1000));
        textEl.setAttribute('x', String(Math.round((bb.maxX + pad) * 1000) / 1000));
        textEl.removeAttribute('dominant-baseline');
      });
      svg.querySelectorAll('path[d]').forEach((pathEl) => {
        const seg = this.parseHorizontalLegendLineSegment(pathEl.getAttribute('d') || '');
        if (!seg) {
          return;
        }
        const p1 = pathEl.parentElement;
        if (!p1 || !p1.nextElementSibling) {
          return;
        }
        const textEl = p1.nextElementSibling.querySelector && p1.nextElementSibling.querySelector('text');
        if (!textEl || !(textEl.textContent || '').trim()) {
          return;
        }
        const fs = parseFloat(String(textEl.getAttribute('font-size') || '12').replace(/px/gi, '')) || 12;
        const officeBaselineNudge = fs * 0.4;
        textEl.setAttribute('y', String(Math.round((seg.y + officeBaselineNudge) * 1000) / 1000));
        textEl.setAttribute('x', String(Math.round((seg.x2 + pad) * 1000) / 1000));
        textEl.removeAttribute('dominant-baseline');
      });
      return new XMLSerializer().serializeToString(svg);
    },
    normalizeSvgForOffice(svgString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement;

      // 1) normalize fills/strokes (you already do this)
      svg.querySelectorAll('*').forEach(el => {
        ['fill', 'stroke', 'stop-color'].forEach(attr => this.fixPaintAttr(el, attr));
        const style = el.getAttribute('style');
        if (style) {
          let newStyle = style
            .replace(/rgb\(\s*(\d+)\s+(\d+)\s+(\d+)\s*\)/gi, (_m, r, g, b) => `rgb(${r},${g},${b})`)
            .replace(/rgba\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s\/]+([0-9.]+))\s*\)/gi,
              (_m, r, g, b) => this.rgbPartsToHex(r, g, b));
          el.setAttribute('style', newStyle);
        }
      });

      // 2) ***SALVAGE*** bad paths instead of deleting them
      this.sanitizeNaNPaths(svg);

      // 3) Pie 100% slice: Office-safe <circle> instead of degenerate arc path
      this.replaceDegenerateFullCirclePiePaths(svg);

      // Optional: fix text attrs as you do elsewhere
      svg.querySelectorAll('text').forEach(t => {
        if (t.getAttribute('text-decoration') === 'undefined') t.removeAttribute('text-decoration');
        if (t.getAttribute('dominant-baseline') === 'central') t.setAttribute('dominant-baseline', 'middle');
      });

      const serializer = new XMLSerializer();
      return serializer.serializeToString(svg);
    }
  }
}